import type { IScope } from "angular";
import "angular-translate";
import { catchError, concatMap, finalize, map } from "rxjs/operators";
import type { Observable } from "rxjs";
import { throwError as observableThrowError } from "rxjs";
import { clone, cloneDeep, some } from "lodash";
import "@progress/kendo-ui/js/kendo.autocomplete";

import type { IAccountRepository } from "App/Modules/Auth";
import type {
    IEnumService,
    ILabel,
    IPartnerUserFlat,
    IPartnerUserInsert,
    IPartnerUserSlim,
    IResponse,
    IResponseWithLoading,
    IRole,
    ITeam,
    ITeamMember,
} from "App/Modules/Models";
import { RoleFunctions } from "App/Modules/Models";
import type { IPartnerUsersRepository, IRolesRepository, ITeamsRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services";

enum AddUserDialogStates {
    SelectUser,
    DefineUserParameters,
    DefineUserRole,
}

export default class AddUserToTeamComponentController {
    public static $inject: string[] = [
        "$scope",
        "$translate",
        "irRolesRepository",
        "mmTeamsRepository",
        "mmPartnerUsersRepository",
        "mmEnumService",
        "mmAccountRepository",
    ];

    public team: ITeam;

    public state: AddUserDialogStates = AddUserDialogStates.SelectUser;

    public isNew: boolean;

    public user: IPartnerUserSlim;

    public originalUser: IPartnerUserSlim;

    public errorMessage: string;

    public addResponse: IResponseWithLoading<ITeamMember>;

    public createLogin: boolean;

    public enableTwoFactorAuth: boolean;

    public siteRoles: IRole[];

    public partnerUserRole: IRole;

    public labels: string[];

    public partnerUserLabelsString: string;

    public rolesResponse: IResponseWithLoading<IRole[]>;

    public labelsResponse: IResponseWithLoading<ILabel[]>;

    public teamMembersResponse: IResponseWithLoading<ITeamMember[]>;

    public existingUsers: string[] = [];

    public resolve: IFormContainerResolve<ITeamMember, ITeam>;

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly rolesRepository: IRolesRepository,
        private readonly teamsRepository: ITeamsRepository,
        private readonly partnerUsersRepository: IPartnerUsersRepository,
        private readonly enumService: IEnumService,
        private readonly accountRepository: IAccountRepository,
    ) {}

    public $onInit() {
        this.team = this.resolve.bindings;
        this.enableTwoFactorAuth = false;

        extendWithLoadingAndFail(this.teamsRepository.getTeamMembers(this.team.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.teamMembersResponse = response;
                    if (!response.hasError && response.data) {
                        const ids = response.data.map((x) => x.partnerUser.id);
                        this.existingUsers = ids;
                    }
                },
                (error) => {
                    this.teamMembersResponse = error;
                },
            );

        extendWithLoadingAndFail(this.rolesRepository.getSiteRoles())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.rolesResponse = response;
                    if (!response.hasError && response.data) {
                        const roles = [this.rolesRepository.getNoRole()].concat(response.data);
                        this.siteRoles = roles;
                        this.partnerUserRole = roles[0];
                    }
                },
                (error) => {
                    this.rolesResponse = error;
                },
            );

        extendWithLoadingAndFail(this.partnerUsersRepository.getLabels(this.team.partnerId))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.labelsResponse = response;
                    if (!response.hasError && response.data) {
                        this.labels = response.data.map((l) => l.label);
                    }
                },
                (error) => {
                    this.labelsResponse = error;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public sortById(role: IRole) {
        return RoleFunctions.sortById(role);
    }

    public isLoading(): boolean {
        return some(
            [this.labelsResponse, this.rolesResponse, this.teamMembersResponse],
            (item) => item.isLoading === true,
        );
    }

    public newUser(searchString: string) {
        this.isNew = true;
        this.user = {} as IPartnerUserSlim;

        const isEmail = /^[\w+.-]+@[\d.A-Za-z-]+$/;
        const isPhoneNumber = /^[\d +.-]+$/;

        if (isEmail.test(searchString)) {
            this.user.email = searchString;
        } else if (isPhoneNumber.test(searchString)) {
            this.user.phoneNumber = searchString;
            this.user.smsNumber = searchString;
        } else {
            this.user.fullName = searchString;
        }
        this.state = AddUserDialogStates.DefineUserParameters;
    }

    public userSelected(user: IPartnerUserSlim) {
        this.isNew = false;
        this.originalUser = cloneDeep(user);
        this.user = user;
        this.state = AddUserDialogStates.DefineUserParameters;
    }

    public next() {
        this.state = AddUserDialogStates.DefineUserRole;
    }

    public add() {
        let userObservable: Observable<IResponse<IPartnerUserSlim>>;
        if (this.isNew) {
            userObservable = this.createUser(this.user);
        } else {
            userObservable = this.patchUser(this.user);
        }

        extendWithLoadingAndFail(
            userObservable.pipe(
                concatMap((userResponse: IResponse<IPartnerUserSlim>) => {
                    const teamMember: any = {
                        labels: null,
                        notificationSubscriptions: null,
                        partnerUser: {
                            id: userResponse.data.id,
                        },
                        role:
                            this.partnerUserRole && this.partnerUserRole.id
                                ? {
                                      id: this.partnerUserRole.id,
                                  }
                                : null,
                    };

                    if (this.partnerUserLabelsString) {
                        teamMember.labels = this.partnerUserLabelsString
                            .split(",")
                            .map((l) => l.trim())
                            .filter((l) => l !== "")
                            .map((l) => ({ label: l }));
                    }

                    return this.teamsRepository.addTeamMember(this.team.id, teamMember);
                }),
            ),
        ).subscribe(
            (response: IResponseWithLoading<ITeamMember>) => {
                this.addResponse = response;
                if (!response.isLoading && response.data) {
                    this.existingUsers.push(response.data.partnerUser.id);
                    this.resolve.options.onOk(response.data);
                }
            },
            (error) => {
                this.addResponse = error;
            },
        );
    }

    private createUser(user: IPartnerUserSlim): Observable<IResponse<IPartnerUserSlim>> {
        const partnerUserInsert = {
            displayName: user.fullName,
            email: user.email,
            enableTwoFactorAuth: this.enableTwoFactorAuth,
            fullName: user.fullName,
            partnerId: this.team.partnerId,
            phoneNumber: user.phoneNumber,
            smsNumber: user.smsNumber,
            userId: user.id,
        } as IPartnerUserInsert;

        let result = this.partnerUsersRepository.addPartnerUser(this.team.partnerId, partnerUserInsert).pipe(
            catchError((err: IResponse<void>) => {
                err.error = {
                    // Does not follow typings
                    message: "Failed to create user",
                    serverError: err.error,
                } as any;
                return observableThrowError(err);
            }),
        );

        if (this.createLogin) {
            result = result.pipe(
                concatMap((pu: IResponse<IPartnerUserSlim>) =>
                    this.partnerUsersRepository.verify(this.team.partnerId, pu.data.id, this.enableTwoFactorAuth).pipe(
                        catchError((err: IResponse<void>) => {
                            err.error = {
                                // Does not follow typings
                                message: "Failed to verify user",
                                serverError: err.error,
                            } as any;
                            return observableThrowError(err);
                        }),
                        map(() => pu),
                    ),
                ),
            );

            if (this.enableTwoFactorAuth) {
                result = result.pipe(
                    concatMap((pu: IResponse<IPartnerUserSlim>) =>
                        this.accountRepository.twoFactorAuthentication(pu.data.email).pipe(
                            catchError((err: IResponse<void>) => {
                                err.error = {
                                    // Does not follow typings
                                    message: "Failed to create user",
                                    serverError: err.error,
                                } as any;
                                return observableThrowError(err);
                            }),
                            map(() => pu),
                        ),
                    ),
                );
            }
        }

        return result;
    }

    private patchUser(user: IPartnerUserSlim): Observable<IResponse<IPartnerUserSlim>> {
        const patchObject = {} as IPartnerUserFlat;
        if (this.originalUser.phoneNumber !== user.phoneNumber) {
            patchObject.phoneNumber = user.phoneNumber;
        }
        if (this.originalUser.smsNumber !== user.smsNumber) {
            patchObject.smsNumber = user.smsNumber;
        }

        return this.partnerUsersRepository.patchPartnerUser(user.partner.id, user.id, patchObject).pipe(
            map((response: IResponse<IPartnerUserFlat>) => {
                user.phoneNumber = response.data.phoneNumber;
                user.smsNumber = response.data.smsNumber;
                const userResponse = clone(response) as any as IResponse<IPartnerUserSlim>;
                userResponse.data = user;
                return userResponse;
            }),
        );
    }
}
